import React, { useEffect, useRef } from 'react';
import './Matching.css';
import './JobPoolDelete.css';
import './JobPoolScope.css';
import { formatToJSTMonthDayTime } from '../../utils/dateFormatter.js';
import { FOCUS_LIST_TOP } from './useJobPoolDelete';
import { SCOPE_ALL, SCOPE_OWN } from './jobPoolScope';

// 削除の操作を渡されないとき (削除を使わない呼び出し元) の既定
const NO_DELETE = { canDeleteItem: () => false };
// 印の判定を渡されないときの既定 (印を出さない)
const NO_MARK = () => false;

/**
 * マッチングの対象にする求人の一覧 (SCR-M-001 §4.6、IN-M-007・008・013、DP-M-008・009・011・020・021、
 * Issue #779・#782)
 *
 * 表示する求人 (items。IN-M-013 で絞ったもの) を、取込の新しい順に表示し、行ごとのチェックで対象を選ぶ。
 * scope を渡すと、見出しの下に「この求職者のために上げた求人だけ」と「おすすめも含めて全部」の切り替えを出す (§4.6.6)。
 * 置き場所に依存しない部品として作る (§4.6.1 論点 12。#775 で案件詳細に移す場合も振る舞いは変えない)。
 * 選択の状態は useAiMatching が持ち、本コンポーネントは表示と操作の受け渡しだけを行う。
 *
 * @param {{
 *   items: object[],
 *   loading: boolean,
 *   errorMessage: string|null,
 *   selectedIdSet: Set<string>,
 *   selectedCount: number,
 *   selectionState: 'all'|'some'|'none',
 *   onToggleItem: (id: string) => void,
 *   onToggleAll: () => void,
 *   scope?: {value: 'own'|'all', onChange: (value: string) => void, totalCount: number},
 *   isRecommended?: (item: object) => boolean,
 *   deletion?: {
 *     canDeleteItem: (item: object) => boolean,
 *     disabled?: boolean,
 *     onDelete?: (item: object) => void,
 *     statusMessage?: string|null,
 *     focusRequest?: string|null,
 *     onFocusHandled?: () => void,
 *   },
 * }} props
 *   scope: 表示する求人の切り替え (IN-M-013)。totalCount はテナントの求人の件数 (0 なら切り替えを出さない)。
 *   isRecommended: 「おすすめ」の印 (DP-M-020) を付けるか。
 *   deletion: 求人プールからの削除 (BTN-M-014。§4.3・§6.2、Issue #883)。canDeleteItem が偽の行には「削除」を出さない
 *   (権限の無い求人。押せなくして理由を出す形にしない。求人ごとの判定は Issue #782)。disabled の間は出していても
 *   押せない。focusRequest は削除の後にフォーカスを移す行の求人の ID か FOCUS_LIST_TOP
 */
function JobSelectionList({
    items,
    loading,
    errorMessage,
    selectedIdSet,
    selectedCount,
    selectionState,
    onToggleItem,
    onToggleAll,
    scope = null,
    isRecommended = NO_MARK,
    deletion = NO_DELETE,
}) {
    const selectAllRef = useRef(null);
    const legendRef = useRef(null);
    const countRef = useRef(null);
    // 行のチェックボックス (削除の後にフォーカスを移すため。求人の ID ごと)
    const checkboxRefs = useRef(new Map());
    const { focusRequest, onFocusHandled } = deletion;

    // 一部選択は indeterminate で表す (§4.6.4。属性では指定できないため DOM に設定する)
    useEffect(() => {
        if (selectAllRef.current) {
            selectAllRef.current.indeterminate = selectionState === 'some';
        }
    }, [selectionState, items.length]);

    // 削除の後のフォーカス (§6.2 手順 3): 取り除いた行の次の行 (最後の行なら前の行) のチェックボックスへ。
    // 行が無くなれば一覧の見出しへ移すが、0 件のときは見出しを出さないため、求人プールの件数 (DP-M-008) へ移す
    useEffect(() => {
        if (!focusRequest) return;
        const target =
            focusRequest === FOCUS_LIST_TOP
                ? legendRef.current || countRef.current
                : checkboxRefs.current.get(focusRequest);
        target?.focus();
        onFocusHandled?.();
    }, [focusRequest, onFocusHandled, items]);

    if (loading) {
        return <p className="job-pool-summary-loading">求人プールを取得中...</p>;
    }

    if (errorMessage) {
        return <p className="job-pool-summary-error" role="alert">{errorMessage}</p>;
    }

    // DP-M-008・011 は表示している求人で数える (§4.6.6 #11)
    const total = items.length;
    // テナントの求人の件数。scope を渡さない呼び出し元では表示している件数と同じ
    const poolTotal = scope ? scope.totalCount : total;

    return (
        <div className="job-pool-summary">
            {/* DP-M-008 */}
            <p className="job-pool-summary-count" ref={countRef} tabIndex={-1}>求人プール: {total} 件</p>

            {/* 削除できたときの文言 (§6.2 手順 3)。領域ごと出し入れすると読み上げられないことがあるため、
                削除を使えるときは常に置く (削除できる求人は行ごとに決まるため、最後の 1 件を消しても領域を残す。Issue #782) */}
            {deletion.onDelete && (
                <p className="job-pool-delete-status" role="status">{deletion.statusMessage}</p>
            )}

            {poolTotal === 0 ? (
                <p className="no-data">求人プールに求人が登録されていません。</p>
            ) : (
                <fieldset className="job-selection">
                    <legend className="job-selection-legend" ref={legendRef} tabIndex={-1}>
                        マッチングの対象にする求人
                    </legend>
                    {scope && <JobPoolScopeSwitch scope={scope} />}
                    {total === 0 ? (
                        // DP-M-021 (§4.6.6 #8)
                        <p className="job-selection-own-empty">
                            この求職者のために上げた求人はありません。「おすすめも含めて全部」に切り替えると、ほかの求人を選べます。
                        </p>
                    ) : (
                    <div className="job-selection-frame">
                        {/* 見出し行 (スクロール領域の上に固定。§4.6.2) */}
                        <div className="job-selection-header">
                            <label className="job-selection-select-all">
                                <input
                                    ref={selectAllRef}
                                    type="checkbox"
                                    checked={selectionState === 'all'}
                                    onChange={onToggleAll}
                                />
                                すべて選択
                            </label>
                            {/* DP-M-011: 常に表示し、一部のときだけ強調する (§4.6.1 論点 1) */}
                            <span
                                className={`job-selection-count${selectionState === 'all' ? '' : ' job-selection-count--partial'}`}
                                aria-live="polite"
                            >
                                {selectionState === 'all'
                                    ? `${total}件中${total}件を選択（すべて対象）`
                                    : `${total}件中${selectedCount}件を選択`}
                            </span>
                        </div>

                        <ul className="job-selection-list">
                            {items.map((item) => (
                                <JobSelectionRow
                                    key={item.id}
                                    item={item}
                                    selected={selectedIdSet.has(item.id)}
                                    onToggle={onToggleItem}
                                    recommended={isRecommended(item)}
                                    deletable={deletion.canDeleteItem(item)}
                                    deletion={deletion}
                                    checkboxRef={(el) => {
                                        if (el) checkboxRefs.current.set(item.id, el);
                                        else checkboxRefs.current.delete(item.id);
                                    }}
                                />
                            ))}
                        </ul>
                    </div>
                    )}
                </fieldset>
            )}
        </div>
    );
}

/**
 * 表示する求人の切り替え (IN-M-013。§4.6.2・§4.6.4・§4.6.6、Issue #782)。標準のラジオボタン 2 つ。
 */
function JobPoolScopeSwitch({ scope }) {
    const options = [
        { value: SCOPE_OWN, label: 'この求職者のために上げた求人だけ' },
        { value: SCOPE_ALL, label: 'おすすめも含めて全部' },
    ];
    return (
        <div className="job-selection-scope">
            <fieldset className="job-selection-scope-options">
                <legend className="job-selection-visually-hidden">表示する求人</legend>
                {options.map((option) => (
                    <label key={option.value} className="job-selection-scope-option">
                        <input
                            type="radio"
                            name="job-pool-scope"
                            value={option.value}
                            checked={scope.value === option.value}
                            onChange={() => scope.onChange(option.value)}
                        />
                        {option.label}
                    </label>
                ))}
            </fieldset>
            <p className="job-selection-scope-note">
                「おすすめ」は、ほかの求職者のために上げた求人と、ほかの担当者が上げた求人です。
            </p>
        </div>
    );
}

/**
 * 一覧の行 (DP-M-009)。行全体を label にして、文字を押してもチェックが切り替わるようにする (§4.6.2)。
 * 「削除」(BTN-M-014。Issue #883) は label の外 (行の右端) に置き、押しても行のチェックは切り替わらない。
 */
function JobSelectionRow({ item, selected, onToggle, recommended, deletable, deletion, checkboxRef }) {
    // 業界は複数値 (配列) を「、」でつなぐ (§4.6.1 論点 5。#781 で配列になる)
    const industry = Array.isArray(item.industry) ? item.industry.join('、') : '';
    const importedAt = formatToJSTMonthDayTime(item.created_at);
    const rowClass = [
        'job-selection-row',
        selected ? 'job-selection-row--selected' : '',
        deletable ? 'job-selection-row--deletable' : '',
    ]
        .filter(Boolean)
        .join(' ');

    return (
        <li className={rowClass}>
            <label className="job-selection-row-label">
                <input
                    ref={checkboxRef}
                    type="checkbox"
                    className="job-selection-row-checkbox"
                    checked={selected}
                    onChange={() => onToggle(item.id)}
                />
                <span className="job-selection-row-main">
                    <span className="job-selection-row-company">{item.company_name}</span>
                    <span className="job-selection-row-title">{item.job_title}</span>
                    {/* DP-M-020 (§4.6.6 #3、Issue #782) */}
                    {recommended && <span className="job-pool-recommended-mark">おすすめ</span>}
                </span>
                <span className="job-selection-row-sub">
                    {industry && (
                        <span className="job-selection-row-industry" title={industry}>{industry}</span>
                    )}
                    {importedAt && <span className="job-selection-row-date">{importedAt}</span>}
                    {item.version > 1 && <span className="job-selection-row-version">v{item.version}</span>}
                </span>
            </label>
            {deletable && (
                <button
                    type="button"
                    className="job-selection-row-delete"
                    aria-label={`「${item.company_name} ${item.job_title}」を求人プールから削除`}
                    disabled={deletion.disabled}
                    onClick={() => deletion.onDelete?.(item)}
                >
                    削除
                </button>
            )}
        </li>
    );
}

export default JobSelectionList;
